import type { TdPmsTablePeriodColumnSignature } from 'lumen/components/hotel/channel-pricing/tables/pms-table';
import type { Carate, Carecommendation, PriceAction } from 'lumen/models';

export function logChannelsTablePricingCellDebugValues(
  date: string,
  hotelInfoId: string,
  latestPushedPriceAction: PriceAction | null,
  carate: Carate,
  isRateDataValidForClosingTheLoop: boolean,
): void {
  if (!console) {
    return;
  }

  // Debug logging - only for staff users
  // prettier-ignore-start
  console.group('🔍 Channel Pricing Cell Debug Info');
  console.log('📅 Date:', date);
  console.log('🏨 Hotel Info ID:', hotelInfoId);
  console.group('💰 Price Action Info');
  console.log('Latest Relevant Pushed Price Action:', latestPushedPriceAction);
  console.log(
    'Latest Relevant Pushed Price Action Created At:',
    latestPushedPriceAction?.createdAt?.format('YYYY-MM-DD HH:mm:ss') || 'N/A',
  );
  console.groupEnd();
  console.group('🕒 Extract DateTime Info');
  console.log(
    'Carate Extract DateTime:',
    carate?.extractDatetime?.format('YYYY-MM-DD HH:mm:ss') || 'N/A',
  );
  const isRateExtractDateTimeMoreRecent = latestPushedPriceAction
    ? (carate.extractDatetime?.isAfter(latestPushedPriceAction.createdAt) ??
      false)
    : 'N/A (no price action)';
  console.log(
    'Is Extract DateTime more recent than Price Action?',
    isRateExtractDateTimeMoreRecent,
  );
  console.log(
    'Is Rate Data Valid For Closing The Loop?:',
    isRateDataValidForClosingTheLoop,
  );
  console.groupEnd();
  console.group('💸 Rate Comparison');
  if (carate) {
    console.log('Carate Value:', carate.value);
    console.log('Carate Shopped Value:', carate.shoppedValue);
    console.log('Note: we strip decimals before comparing');
    console.log('Is Rate Discount Detected?', carate.isRateDiscountDetected);
    console.log(
      'Is Rate Shopped Value As Expected?:',
      carate.isRateShoppedValueAsExpected,
    );
    console.log('Carate Room name:', carate.roomName);
    console.log('Carate Message:', carate.message || 'no message');
    console.dir(carate);
  }
  console.groupEnd();
  console.groupEnd();
  // prettier-ignore-end
}

export function logPmsTablePricingCellDebugValues(
  columnId: string,
  hasRecommendation: boolean,
  isAutopilotEnabled: boolean,
  showException: boolean,
  hasInconsistencyInRate: boolean | undefined,
  carate: Carate | undefined,
  recommendation: Carecommendation | null | undefined,
): void {
  if (!console) {
    return;
  }

  // prettier-ignore-start
  console.group('🔍 PMS Pricing Cell Debug Info');
  console.log('📅 Column:', columnId);
  console.group('🎯 State Flags');
  console.log('hasRecommendation:', hasRecommendation);
  console.log('isAutopilotEnabled:', isAutopilotEnabled);
  console.log('showException:', showException);
  console.log('hasInconsistencyInRate:', hasInconsistencyInRate);
  console.groupEnd();
  console.group('📦 Models');
  if (carate) {
    console.group('Carate:');
    console.log('Room name:', carate.roomName);
    console.log('PMS value:', carate.pmsValue);
    console.log('Message:', carate.message || 'no message');
    console.dir(carate);
    console.groupEnd();
  }
  if (recommendation) {
    console.group('Carecommendation:');
    console.log('recommended PMS value:', recommendation.recommendedPmsValue);
    console.dir(recommendation);
    console.groupEnd();
  }
  console.groupEnd();
  console.groupEnd();
  // prettier-ignore-end
}

export function logPmsTableRestrictionsCellDebugValues(
  columnId: string,
  hasRestriction: boolean,
  hasRestrictionRecommendation: boolean,
  showException: boolean,
  restriction: TdPmsTablePeriodColumnSignature['restrictions'][0] | undefined,
  restrictionRecommendation:
    | TdPmsTablePeriodColumnSignature['restrictionsRecommendations'][0]
    | undefined,
): void {
  if (!console) {
    return;
  }

  // prettier-ignore-start
  console.group('🔍 PMS Restrictions Cell Debug Info');
  console.log('📅 Column:', columnId);
  console.group('🎯 State Flags');
  console.log('hasRestriction:', hasRestriction);
  console.log('hasRestrictionRecommendation:', hasRestrictionRecommendation);
  console.log('showException:', showException);
  console.groupEnd();
  console.group('📦 Models');

  if (restriction) {
    console.group('Restriction:');
    console.log('formattedValues:', restriction.formattedValues);
    console.dir(restriction);
    console.groupEnd();
  }
  if (restrictionRecommendation) {
    console.group('Restriction Recommendation:');
    console.log('formattedValues:', restrictionRecommendation.formattedValues);
    console.dir(restrictionRecommendation);
    console.groupEnd();
  }
  console.groupEnd();
  console.groupEnd();
  // prettier-ignore-end
}

export function logChannelsTableRestrictionsCellDebugValues(
  columnId: string,
  hasRestriction: boolean,
  hasRestrictionRecommendation: boolean,
  showException: boolean,
  restriction: TdPmsTablePeriodColumnSignature['restrictions'][0] | undefined,
  restrictionRecommendation:
    | TdPmsTablePeriodColumnSignature['restrictionsRecommendations'][0]
    | undefined,
): void {
  if (!console) {
    return;
  }

  // prettier-ignore-start
  console.group('🔍 Channels Restrictions Cell Debug Info');
  console.log('📅 Column:', columnId);
  console.group('🎯 State Flags');
  console.log('hasRestriction:', hasRestriction);
  console.log('hasRestrictionRecommendation:', hasRestrictionRecommendation);
  console.log('showException:', showException);
  console.groupEnd();
  console.group('📦 Models');

  if (restriction) {
    console.group('Restriction:');
    console.log('channel ID:', restriction.channelId);
    console.log('formattedValues:', restriction.formattedValues);
    console.dir(restriction);
    console.groupEnd();
  }
  if (restrictionRecommendation) {
    console.group('Restriction Recommendation:');
    console.log('formattedValues:', restrictionRecommendation.formattedValues);
    console.dir(restrictionRecommendation);
    console.groupEnd();
  }
  console.groupEnd();
  console.groupEnd();
  // prettier-ignore-end
}

// Default export for backwards compatibility
export default logChannelsTablePricingCellDebugValues;
